
/**
 * BackToTop
 *
 * 正常交互：
 * 1. 鼠标点击 -> 返回顶部
 * 2. 手机触摸 -> 返回顶部
 * 3. Enter -> keydown 返回顶部
 * 4. Space -> keyup 返回顶部
 * 5. ESC -> 不触发返回顶部
 * 6. ESC -> 关闭当前弹窗
 * 7. 弹窗打开时禁止返回顶部
 */

(() => {
  'use strict';

  // =====================================
  // 弹窗检测
  // =====================================

  const popupSelector = [
    'dialog[open]',
    'modal-dialog[open]',
    'product-modal[open]',
    'quick-add-modal[open]',
    '[data-esc-close="true"]',
    '.modal.is-open',
    '.modal.show',
    '.popup.is-open',
    '.popup.show'
  ].join(',');

  const isVisible = (element) => {
    if (!element || !element.isConnected) {
      return false;
    }

    if (element.hidden) return false;

    if (
      element.getAttribute('aria-hidden') === 'true'
    ) {
      return false;
    }

    const style = getComputedStyle(element);

    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      element.getClientRects().length > 0
    );
  };

  const getActivePopup = () => {
    const popups = Array.from(
      document.querySelectorAll(popupSelector)
    ).filter(isVisible);

    if (!popups.length) return null;

    // 优先选择当前焦点所属的弹窗
    const activeElement = document.activeElement;

    if (activeElement?.closest) {
      const focusedPopup = activeElement.closest(
        popupSelector
      );

      if (focusedPopup && popups.includes(focusedPopup)) {
        return focusedPopup;
      }
    }

    // 否则优先使用最后一个可见弹窗
    return popups[popups.length - 1];
  };

  // =====================================
  // 返回顶部 Web Component
  // =====================================

  if (!customElements.get('back-to-top')) {
    class BackToTop extends HTMLElement {
      constructor() {
        super();

        this.pageHeight = window.innerHeight;

        this.rafId = null;
        this.resizeTimer = null;
        this.spacePressed = false;
        this.hasNativeButton = false;

        this.onClick = this.onClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.checkVisible = this.checkVisible.bind(this);
        this.handleResize = this.handleResize.bind(this);
      }

      connectedCallback() {
        this.pageHeight = window.innerHeight;

        // 如果内部有原生按钮或链接，
        // 应交给浏览器处理其 Enter / Space 行为
        this.hasNativeButton = Boolean(
          this.querySelector(
            'button, a[href], input[type="button"], input[type="submit"]'
          )
        );

        // 没有原生按钮时，让自定义元素可聚焦
        if (!this.hasNativeButton) {
          if (!this.hasAttribute('tabindex')) {
            this.setAttribute('tabindex', '0');
          }

          if (!this.hasAttribute('role')) {
            this.setAttribute('role', 'button');
          }

          if (!this.hasAttribute('aria-label')) {
            this.setAttribute('aria-label', '返回顶部');
          }
        }

        this.addEventListener('click', this.onClick);
        this.addEventListener('keydown', this.onKeyDown);
        this.addEventListener('keyup', this.onKeyUp);
        this.addEventListener('blur', this.onBlur);

        // 滚动监听采用 rAF 合并更新
        window.addEventListener(
          'scroll',
          this.checkVisible,
          { passive: true }
        );

        // resize 使用 150ms 防抖
        window.addEventListener(
          'resize',
          this.handleResize,
          { passive: true }
        );

        this.checkVisible();
      }

      disconnectedCallback() {
        // Theme Editor 移除 Section 时清理事件
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('keydown', this.onKeyDown);
        this.removeEventListener('keyup', this.onKeyUp);
        this.removeEventListener('blur', this.onBlur);

        window.removeEventListener(
          'scroll',
          this.checkVisible
        );

        window.removeEventListener(
          'resize',
          this.handleResize
        );

        if (this.rafId !== null) {
          cancelAnimationFrame(this.rafId);
          this.rafId = null;
        }

        clearTimeout(this.resizeTimer);
        this.spacePressed = false;
      }

      // =====================================
      // 判断是否允许返回顶部
      // =====================================

      canScrollToTop() {
        // 有弹窗打开时，不允许返回顶部
        if (getActivePopup()) {
          return false;
        }

        return true;
      }

      // =====================================
      // 鼠标 / 触摸点击
      // =====================================

      onClick(event) {
        // 只接受真实用户触发的 click，
        // 包括浏览器生成的键盘激活 click
        if (!event.isTrusted) return;

        if (!this.canScrollToTop()) {
          return;
        }

        if (
          typeof event.button === 'number' &&
          event.button !== 0
        ) {
          return;
        }

        // 避免内部链接的默认跳转
        if (event.cancelable) {
          event.preventDefault();
        }

        this.scrollToTop();
      }

      // =====================================
      // Enter / Space Keydown
      // =====================================

      onKeyDown(event) {
        // 如果内部使用原生按钮或链接，
        // 不重复实现浏览器已有的键盘激活行为
        if (this.hasNativeButton) return;

        // 仅处理组件自身获得焦点的情况
        if (event.target !== this) return;

        // Enter：keydown 时立即执行
        if (event.key === 'Enter') {
          event.preventDefault();

          // 忽略长按产生的重复事件
          if (event.repeat) return;

          if (!this.canScrollToTop()) return;

          this.scrollToTop();
          return;
        }

        // Space：keydown 只阻止默认页面滚动
        if (event.key === ' ' || event.key === 'Spacebar') {
          event.preventDefault();

          if (!event.repeat) {
            this.spacePressed = true;
          }

          return;
        }

        // ESC 不在此处理，也不触发返回顶部
      }

      // =====================================
      // Space Keyup
      // =====================================

      onKeyUp(event) {
        if (this.hasNativeButton) return;
        if (event.target !== this) return;

        if (
          event.key !== ' ' &&
          event.key !== 'Spacebar'
        ) {
          return;
        }

        event.preventDefault();

        if (!this.spacePressed) return;

        this.spacePressed = false;

        if (!this.canScrollToTop()) return;

        this.scrollToTop();
      }

      // 失去焦点时取消空格键激活状态
      onBlur() {
        this.spacePressed = false;
      }

      // =====================================
      // 返回顶部
      // =====================================

      scrollToTop() {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: window.matchMedia(
            '(prefers-reduced-motion: reduce)'
          ).matches ? 'auto' : 'smooth'
        });
      }

      // =====================================
      // 滚动显示控制
      // =====================================

      checkVisible() {
        if (this.rafId !== null) return;

        this.rafId = requestAnimationFrame(() => {
          this.rafId = null;

          if (!this.isConnected) return;

          const y = window.scrollY;

          this.classList.toggle(
            'back-to-top--active',
            y > this.pageHeight
          );
        });
      }

      // =====================================
      // 窗口变化防抖
      // =====================================

      handleResize() {
        clearTimeout(this.resizeTimer);

        this.resizeTimer = setTimeout(() => {
          this.pageHeight = window.innerHeight;
          this.checkVisible();
          this.resizeTimer = null;
        }, 150);
      }
    }

    customElements.define('back-to-top', BackToTop);
  }

  // =====================================
  // ESC 关闭弹窗
  // =====================================

  // 避免 Shopify Theme Editor 重复注册监听
  if (!window.__backToTopEscHandlerInstalled) {
    window.__backToTopEscHandlerInstalled = true;

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') return;

        const popup = getActivePopup();

        if (!popup) return;

        // 原生模态 dialog 交给浏览器处理 ESC，
        // 这样可以保留 cancel 事件以及原生关闭行为
        if (
          popup instanceof HTMLDialogElement &&
          popup.matches(':modal')
        ) {
          return;
        }

        let closed = false;

        // 非模态原生 dialog
        if (popup instanceof HTMLDialogElement) {
          popup.close();
          closed = true;
        }

        // Dawn / 主题自定义弹窗
        else if (typeof popup.hide === 'function') {
          popup.hide();
          closed = true;
        }

        else if (typeof popup.close === 'function') {
          popup.close();
          closed = true;
        }

        else {
          // 优先调用弹窗原本的关闭按钮，
          // 保留主题已有动画、焦点和滚动锁处理
          const closeButton = popup.querySelector(
            [
              '[data-popup-close]',
              '[data-modal-close]',
              '[data-close-modal]',
              '[id^="ModalClose-"]',
              '.modal__close',
              '.popup__close',
              '.btn-close'
            ].join(',')
          );

          if (closeButton) {
            closeButton.click();
            closed = true;
          }

          // 仅对主动声明支持 ESC 的简单自定义
          // 弹窗进行隐藏兜底
          else if (
            popup.matches('[data-esc-close="true"]')
          ) {
            popup.classList.remove(
              'is-open',
              'active',
              'open',
              'show'
            );

            popup.setAttribute(
              'aria-hidden',
              'true'
            );

            popup.hidden = true;
            closed = true;
          }
        }

        if (closed) {
          event.preventDefault();

          // 避免同一次 ESC 重复关闭多个弹窗
          event.stopImmediatePropagation();
        }
      },
      true
    );
  }
})();
